import { Link, useNotify, useUpdate } from 'react-admin'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { SearchIcon } from '@icons/utils'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'

import styles from './AccountsList.module.scss'
import sharedStyles from '../MultiPage.module.scss'
import { cn } from '@src/utils/cn.ts'
import { LxIcon } from '@components/icon/Icon.tsx'
import { isNilOrEmpty } from '@src/utils/isNilOrEmpty.ts'
import { useDocumentTitle } from '@src/hooks/useDocumentTitle'
import { ActivitiesIcon, ActivitiesOutlineIcon, QtIcon, SchoolIcon, TeamMemberIcon } from '@src/assets/icons'
import RobotInfo from './RobotInfo/RobotInfo'
import AccountInfo from './AccountInfo'
import { checkAccSettingsScope } from '@src/utils/accountSettings'
import { AccounAddressB } from '@src/logic/contexts/Accounts/AccountsBackend.type'
import { AccounAddressF } from '@src/logic/contexts/Accounts/AccountsFrontend.type'
import { LxClickAbleIcon } from '@src/components/icon/clickAbleIcon'
import { useUserContext } from '@src/logic/contexts/AppStore/UserContext'
import { usePolicyContext } from '@src/logic/contexts/AppStore/AbacContext'
import { AccountActions } from './AccountActions/AccountActions'

export type AccountsListB = {
  account_id: string
  account_name: string
  account_country: string
  customer_id: string
  address: AccounAddressB[]
  tt: string
  tau: string
  tal: string
  lad: string
}

export type AccountsListF = {
  accountId: string
  accountName: string
  accountCountry: string
  customerId: string
  address: AccounAddressF
  totalUsage: string
  totalActiveUsers: string
  totalActiveLearners: string
  lastActiveAt: string
}

export const AccountsList = () => {
  useDocumentTitle('Account Settings')
  const [update] = useUpdate('Report')
  const notify = useNotify()

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [accountData, setAccountData] = useState<AccountsListF | null>(null)
  const [filteredData, seFilteredData] = useState<AccountsListF | null>(null)
  const [sortColumn, setSortColumn] = useState<string>('accountName')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [dialogInfo, setDialogInfo] = useState({ isOpen: false, type: '', account: null })

  const { canAccess: canViewAuditLog, effectiveValue } = checkAccSettingsScope('view_audit_logs')
  let accAuditLogEnabled = effectiveValue === 'true'

  const { currentUser } = useUserContext() as any
  const { canPerformAction } = usePolicyContext()
  const canEditGlobalAccount = canPerformAction(currentUser, { type: 'GlobalAccount' }, 'edit')
  const canEditAccount = canPerformAction(currentUser, { type: 'Account' }, 'edit')

  const sanitizeData = (data) => {
    if (!data || !data.report) {
      return null
    }

    return data.report.map((account: AccountsListB) => ({
      accountId: account.account_id,
      accountName: account.account_name,
      accountCountry: account.account_country,
      customerId: account.customer_id,
      address: account.address[0],
    }))
  }

  const refreshAccounts = useCallback(async () => {
    setIsDataLoading(true)
    try {
      await update(
        'Report',
        {
          id: '1',
          data: { filter: {}, type: 'accounts_lists' },
          previousData: null,
        },
        {
          onSuccess: (data) => {
            const sanitizedData = sanitizeData(data)
            setAccountData(sanitizedData)
            seFilteredData(sanitizedData)
            setIsDataLoading(false)
          },
          onError: (error: any) => {
            setAccountData(null)
            notify(error?.message || 'Something went wrong', { type: 'warning' })
            setIsDataLoading(false)
          },
        }
      )
    } catch {
      setIsDataLoading(false)
    }
  }, [update, notify])

  useEffect(() => {
    refreshAccounts()
  }, [refreshAccounts])

  const handleSearchInputChange = (inputValue: string) => {
    const filteredAccounts = accountData?.filter((account) => account.accountName.toLowerCase().includes(inputValue.toLowerCase()))
    seFilteredData(filteredAccounts)
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('desc')
    }
  }

  const sortedData = filteredData?.sort((a, b) => {
    let columnA = a[sortColumn]
    let columnB = b[sortColumn]
    return sortOrder === 'asc' ? columnA.localeCompare(columnB) : columnB.localeCompare(columnA)
  })

  const pageRef = useRef<HTMLDivElement>(null)

  const openDialog = (type, account) => setDialogInfo({ isOpen: true, type, account })
  const closeDialog = () => setDialogInfo({ isOpen: false, type: '', account: null })

  useEffect(() => {
    // Focus the page when navigated
    pageRef.current?.focus()
  }, [])

  const optionalCols = (canViewAuditLog && accAuditLogEnabled ? 1 : 0) + (canEditGlobalAccount || canEditAccount ? 1 : 0)
  // base columns are: Info, Robots, Users, Audit Logs  => 4
  const gridCols = 4 + optionalCols

  return (
    <>
      <div ref={pageRef} tabIndex={-1} aria-label='Account settings' style={{ outline: 'none' }}>
        <div className={styles.AccountsListHeadingRow}>
          <div className={cn(styles.analyticsListHeading)}>
            <div>
              <LxIcon sxStyles={{ height: '80px', width: '80px' }} icon={ActivitiesIcon}></LxIcon>
            </div>
            <div>
              <h3>Accounts</h3>
            </div>
          </div>
          <div className={cn(sharedStyles.tableActions)}>
            <div className={cn('lxActionButton', 'lxActionButtonDefaultSize', 'inputHighLight')}>
              <LxIcon icon={SearchIcon} />
              <input
                className={'pristineInput'}
                type='text'
                onChange={(e) => {
                  handleSearchInputChange(e.target.value)
                }}
                placeholder='Search'
                aria-label='Search Facilities'
              />
            </div>
          </div>
        </div>
        <div className={cn(styles[`accountListContent-${gridCols}`])}>
          <div className={cn(styles.accountsTableListHeader, styles.facilityName)} onClick={() => handleSort('accountName')}>
            {accountData?.length > 1 ? 'Facilities' : 'Facility'}
          </div>
          <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide2column)}>Info</div>
          <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide3column)}>Robots</div>
          <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide4column)}>Users</div>
          {accAuditLogEnabled && canViewAuditLog && <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide5column)}>Audit Logs</div>}
          {(canEditGlobalAccount || canEditAccount) && <div className={cn(sharedStyles.tableListHeader, sharedStyles.hide6column)}>Actions</div>}
          {isDataLoading ? (
            <LxLoadingSpinner className={sharedStyles.loader} />
          ) : isNilOrEmpty(filteredData) ? (
            <div className={sharedStyles.notFound}>No account match your current search.</div>
          ) : (
            sortedData.map((account: AccountsListF, index) => (
              <React.Fragment key={account.accountId}>
                <div>{account.accountName}</div>
                <div className={sharedStyles.hide5column}>
                  <LxClickAbleIcon icon={SchoolIcon} onClick={() => openDialog('AccountInfo', account)} aria-label={`More info of the account ${account.accountName}`} />
                </div>

                <div className={sharedStyles.hide5column}>
                  <LxClickAbleIcon icon={QtIcon} onClick={() => openDialog('RobotInfo', account)} aria-label={`Robots of the account ${account.accountName}`} />
                </div>
                <div className={sharedStyles.hide6column}>
                  <Link to={`${account.accountId}/users?name=${account.accountName}`} aria-label={`Audit Logs of the account ${account.accountName}`}>
                    <LxIcon icon={TeamMemberIcon} />
                  </Link>
                </div>
                {canViewAuditLog && accAuditLogEnabled && (
                  <div className={sharedStyles.hide7column}>
                    <Link to={`${account.accountId}/auditLogs?name=${account.accountName}`} aria-label={`Audit Logs of the account ${account.accountName}`}>
                      <LxIcon icon={ActivitiesOutlineIcon} />
                    </Link>
                  </div>
                )}
                {(canEditGlobalAccount || canEditAccount) && (
                  <div className={cn(sharedStyles.hide8column, styles.noHoverCell)}>
                    <AccountActions account={account} canEditAccount={canEditAccount} canEditGlobalAccount={canEditGlobalAccount} currentUser={currentUser}  isLastChild={index === sortedData.length - 1} onUpdated={refreshAccounts} />
                  </div>
                )}
              </React.Fragment>
            ))
          )}
        </div>
      </div>
      {dialogInfo.isOpen &&
        {
          RobotInfo: <RobotInfo open onClose={closeDialog} account={dialogInfo.account} />,
          AccountInfo: <AccountInfo open onClose={closeDialog} account={dialogInfo.account} canEditAccount={canEditAccount} canEditGlobalAccount={canEditGlobalAccount} currentUser={currentUser}  onUpdated={refreshAccounts} />,
        }[dialogInfo.type]}
    </>
  )
}
